import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import Select, { components } from "react-select";
import { CheckCircle2, AlertTriangle, ChevronDown } from "lucide-react";
import LanguageToggle from "../../components/Common/LanguageToggle";
import Button from "../../components/Common/Button";
import ErrorMessage from "../../components/Common/ErrorMessage";
import { defaultReactSelectStyles } from "../../utils/reactSelectStyles";
import { getSocialPlatformList } from "../../api/postgre/socialPlatformApi";
import { startSocialVerification, isPlatformVerifiable } from "../../api/socialVerificationApi";
import { getVerificationPresentation } from "../../utils/socialVerification";
import {
    savePendingSocialVerification,
    consumeSocialVerificationResult,
} from "../../utils/socialVerificationSession";
import { showErrorToast } from "../../utils/toast";

// Standalone, minimal social-account ownership-verification screen — platform
// list is fully dynamic (same get_social_media_list API as Register), kept
// outside the full Register form so the real end-to-end OAuth flow for
// whichever platform can be screen-recorded cleanly for app review videos.
const RETURN_TO = "/verify-social";

const selectStyles = {
    ...defaultReactSelectStyles,
    control: (base, state) => ({
        ...defaultReactSelectStyles.control(base),
        borderRadius: "0.75rem",
        backgroundColor: "#f7f7f7",
        borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
        boxShadow: "none",
        minHeight: "48px",
        "&:hover": { borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0" },
    }),
};

const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown className="h-4 w-4 text-slate-400" />
    </components.DropdownIndicator>
);

const VerifySocialAccount = () => {
    const { t } = useTranslation();
    const [platforms, setPlatforms] = useState([]);
    const [result, setResult] = useState(null);
    const [isRedirecting, setIsRedirecting] = useState(false);

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: { platform: "", handle: "" },
    });

    const selectedKey = watch("platform");
    const selectedPlatform = platforms.find((platform) => platform.key === selectedKey) || null;
    const canVerify = isPlatformVerifiable(selectedKey);

    useEffect(() => {
        getSocialPlatformList().then(setPlatforms).catch(() => setPlatforms([]));
        const consumed = consumeSocialVerificationResult();
        if (consumed) setResult(consumed);
    }, []);

    const resultPlatform = result ? platforms.find((platform) => platform.key === result.platform) : null;
    const resultPlatformName = resultPlatform?.name || result?.platform || "";

    const onSubmit = async (values) => {
        const handle = values.handle.trim().replace(/^@/, "");
        setIsRedirecting(true);

        const response = await startSocialVerification({ platform: values.platform, handle, returnTo: RETURN_TO });

        if (response.success && response.redirectUrl) {
            savePendingSocialVerification({ platform: values.platform, handle, returnTo: RETURN_TO });
            window.location.href = response.redirectUrl;
            return;
        }

        setIsRedirecting(false);
        showErrorToast(response.message || t("AUTH_VERIFY_SOCIAL_UNSUPPORTED", "Verification isn't configured for this platform yet."));
    };

    const verification = result ? getVerificationPresentation(result.status) : null;
    const isVerified = result?.status === "verified";

    const platformOptions = platforms.map((platform) => ({ value: platform.key, label: platform.name }));

    return (
        <>
            <div className="fixed top-6 right-6 z-50">
                <LanguageToggle />
            </div>
            <div className="flex min-h-screen items-center justify-center bg-cream px-6 py-12">
                <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                    <img src="/assets/icons/korres-shares-logo.png" alt={t("AUTH_KORRES_SHARES", "KORRES Shares")} className="h-[4.5rem] w-[4.5rem]" />

                    {result ? (
                        <div className="mt-8">
                            <div className={`flex h-12 w-12 items-center justify-center rounded-full ${isVerified ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                                {isVerified ? <CheckCircle2 className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
                            </div>
                            <h1 className="mt-4 text-xl font-semibold text-slate-900">
                                {isVerified
                                    ? `${resultPlatformName} ${t("AUTH_VERIFY_SOCIAL_SUCCESS_TITLE_SUFFIX", "account verified")}`
                                    : verification.label}
                            </h1>
                            {result.handle ? (
                                <p className="mt-1 text-sm font-medium text-slate-700">@{result.handle}</p>
                            ) : null}
                            <p className="mt-2 text-sm text-slate-500">
                                {result.message || verification.helpText}
                            </p>
                            <Button
                                type="button"
                                variant={isVerified ? "secondary" : "primary"}
                                className="mt-6 w-full"
                                onClick={() => setResult(null)}
                            >
                                {isVerified
                                    ? t("AUTH_VERIFY_SOCIAL_VERIFY_ANOTHER", "Verify another account")
                                    : t("AUTH_VERIFY_SOCIAL_TRY_AGAIN", "Try again")}
                            </Button>
                        </div>
                    ) : (
                        <div className="mt-8">
                            <h1 className="text-xl font-semibold text-slate-900">
                                {t("AUTH_VERIFY_SOCIAL_TITLE", "Verify your social account")}
                            </h1>
                            <p className="mt-2 text-sm text-slate-500">
                                {t("AUTH_VERIFY_SOCIAL_SUBTITLE", "Choose a platform and log in to confirm the account is yours.")}
                            </p>

                            <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                        {t("AUTH_VERIFY_SOCIAL_PLATFORM_LABEL", "Platform")}
                                    </label>
                                    <Controller
                                        name="platform"
                                        control={control}
                                        rules={{ required: t("AUTH_VERIFY_SOCIAL_PLATFORM_REQUIRED", "Choose a platform.") }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                value={platformOptions.find((option) => option.value === field.value) || null}
                                                options={platformOptions}
                                                placeholder={t("AUTH_VERIFY_SOCIAL_PLATFORM_PLACEHOLDER", "Select a platform")}
                                                classNamePrefix="react-select"
                                                components={{ DropdownIndicator }}
                                                styles={selectStyles}
                                                isSearchable={false}
                                                onChange={(selected) => field.onChange(selected?.value || "")}
                                            />
                                        )}
                                    />
                                    <ErrorMessage message={errors.platform?.message} />
                                </div>

                                {selectedPlatform ? (
                                    canVerify ? (
                                        <>
                                            <div>
                                                <label htmlFor="social-handle" className="mb-1.5 block text-xs font-semibold text-slate-600">
                                                    {selectedPlatform.name} {t("AUTH_VERIFY_SOCIAL_HANDLE_LABEL_SUFFIX", "handle")}
                                                </label>
                                                <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-input-bg px-4 py-3 focus-within:border-primary">
                                                    <span className="text-sm text-slate-400">@</span>
                                                    <Controller
                                                        name="handle"
                                                        control={control}
                                                        rules={{
                                                            required: t("AUTH_VERIFY_SOCIAL_HANDLE_REQUIRED", "Enter your handle."),
                                                            pattern: {
                                                                value: /^[A-Za-z0-9._]+$/,
                                                                message: t("AUTH_VERIFY_SOCIAL_HANDLE_INVALID", "Use letters, numbers, periods or underscores only."),
                                                            },
                                                        }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                id="social-handle"
                                                                type="text"
                                                                placeholder={t("AUTH_VERIFY_SOCIAL_HANDLE_PLACEHOLDER", "yourhandle")}
                                                                className="w-full bg-transparent text-sm outline-none"
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <ErrorMessage message={errors.handle?.message} />
                                            </div>

                                            <Button type="submit" disabled={!isValid || isRedirecting} className="w-full">
                                                {isRedirecting
                                                    ? `${t("AUTH_VERIFY_SOCIAL_REDIRECTING_PREFIX", "Redirecting to")} ${selectedPlatform.name}…`
                                                    : `${t("AUTH_VERIFY_SOCIAL_CONTINUE_PREFIX", "Continue with")} ${selectedPlatform.name}`}
                                            </Button>
                                        </>
                                    ) : (
                                        <p className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500">
                                            {t("AUTH_VERIFY_SOCIAL_NOT_AVAILABLE_PREFIX", "Verification isn't set up for")} {selectedPlatform.name} {t("AUTH_VERIFY_SOCIAL_NOT_AVAILABLE_SUFFIX", "yet.")}
                                        </p>
                                    )
                                ) : null}
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default VerifySocialAccount;
